import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

import styles from '~/modules/game-engine/drawing/styles';

/** Enough bars to read as a ring rather than as a dial, without a visible seam. */
const BAR_COUNT = 44;

/** Bar size and the clearance between the wordmark's box and the ring, all in px. */
const BAR_WIDTH = 3;
const BAR_LENGTH = 18;
const GAP = 14;

/** How finely the ellipse is walked when spacing the bars evenly along it. */
const ARC_SAMPLES = 1024;

interface Bar {
  x: number;
  y: number;
  /** Degrees, so the bar points straight out of the curve. */
  angle: number;
  duration: string;
  delay: string;
}

/**
 * Bars evenly spaced by arc length rather than by angle. Stepping the angle evenly bunches them up
 * at the ends of the long axis, which on a wordmark-shaped ellipse is most of what you see.
 */
const layOutBars = (a: number, b: number): Bar[] => {
  const lengths = [0];
  for (let index = 1; index <= ARC_SAMPLES; index++) {
    const previous = ((index - 1) / ARC_SAMPLES) * 2 * Math.PI;
    const current = (index / ARC_SAMPLES) * 2 * Math.PI;
    const dx = a * (Math.cos(current) - Math.cos(previous));
    const dy = b * (Math.sin(current) - Math.sin(previous));
    lengths.push(lengths[index - 1] + Math.hypot(dx, dy));
  }
  const perimeter = lengths[ARC_SAMPLES];

  return Array.from({ length: BAR_COUNT }, (_, index) => {
    const target = (index / BAR_COUNT) * perimeter;
    let step = 1;
    while (step < lengths.length && lengths[step] < target) step++;

    const t = ((step - 1) / ARC_SAMPLES) * 2 * Math.PI;
    // Outward normal of the ellipse; +90 because the bar is drawn pointing up.
    const angle = (Math.atan2(a * Math.sin(t), b * Math.cos(t)) * 180) / Math.PI + 90;

    return {
      x: a + BAR_LENGTH + a * Math.cos(t),
      y: b + BAR_LENGTH + b * Math.sin(t),
      angle,
      // Varied so the ring reads as levels rather than as one pulsing shape.
      duration: `${1.2 + (index % 5) * 0.3}s`,
      delay: `${((index * 0.08) % 1.5).toFixed(2)}s`,
    };
  });
};

/**
 * A ring of pulsing audio-level bars drawn *behind* whatever it wraps, at that thing's own size.
 *
 * An ellipse rather than a circle, because the wordmark is wide and short: a circle big enough to
 * go around it would be as tall as it is wide, and this sits above the menu on a screen meant to be
 * read across a room, where every extra pixel of height pushes the menu itself off the bottom.
 *
 * The ring is measured from the rendered content instead of being given a size, so the bars stay
 * the same shape whatever the wordmark's font size resolves to.
 */
export default function LogoRing({ children, className }: PropsWithChildren<{ className?: string }>) {
  const content = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const element = content.current;
    if (!element) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      // Ignore the zero-sized first measurement some browsers report before layout settles.
      if (width > 0 && height > 0) setBox({ width, height });
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const a = box ? box.width / 2 + GAP : 0;
  const b = box ? box.height / 2 + GAP : 0;
  const svgWidth = 2 * (a + BAR_LENGTH);
  const svgHeight = 2 * (b + BAR_LENGTH);
  // Walking the ellipse costs a thousand samples, and the wordmark's box only changes on a resize.
  const bars = useMemo(() => (box ? layOutBars(a, b) : []), [box, a, b]);

  return (
    <div className={`relative grid place-items-center ${className ?? ''}`} data-test="logo-ring">
      {box && (
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          width={svgWidth}
          height={svgHeight}
          aria-hidden
          // Absolute so the ring costs no layout height of its own beyond the wordmark's; the
          // clearance above and below is the only growth.
          className="pointer-events-none absolute opacity-60">
          <defs>
            <linearGradient id="logo-ring-bar" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor={styles.colors.text.active} />
              <stop offset="100%" stopColor="rgba(255, 255, 255, 0.2)" />
            </linearGradient>
          </defs>
          {bars.map(({ x, y, angle, duration, delay }) => (
            <g key={`${x}-${y}`} transform={`translate(${x} ${y}) rotate(${angle})`}>
              <rect
                x={-BAR_WIDTH / 2}
                y={-BAR_LENGTH}
                width={BAR_WIDTH}
                height={BAR_LENGTH}
                rx={BAR_WIDTH / 2}
                fill="url(#logo-ring-bar)"
                className="motion-safe:animate-ak-bar-pulse origin-bottom [transform-box:fill-box]"
                style={{ animationDuration: duration, animationDelay: delay }}
              />
            </g>
          ))}
        </svg>
      )}
      <div ref={content} className="relative">
        {children}
      </div>
    </div>
  );
}
